import qs from 'qs'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { withProps, lifecycle, withHandlers, withStateHandlers } from 'recompose'
import withType from 'components/common/hoc/withType'
import withGBBDetails from 'components/common/hoc/withGBBDetails/withGBBDetails'
import { googleAnalyticProductDetailsView, googleAnalyticPromotionImpressions, googleAnalyticPromotionClicks } from 'actions/googleAnalytic'
import { getActiveVehicleFrontTireSize, getActiveVehicleName } from 'selectors/getActiveVehicle'
import { getPageLocation, getCurrentStore } from 'selectors'
import { getLocationPathname } from 'selectors/getPageLocation'
import { getCurrentStoreNumber } from 'selectors/getCurrentStore'
import { expandBazaarVoiceReviewTabAndScrollDown } from 'helpers/custom-events'
import { generateSchemaDescription, generateSchemaUrl } from 'helpers/SEOSchema'
import BOTSkuDetailComponent from './BOTSkuDetail'
import { showTireDetailsPromotion, updateGBBState, setSkuId } from './actions'
import { getGTMData } from './helpers'
import { TIRE_DETAILS_REVIEWS_QUERY } from '../../../../constant/index'
import isEqual from 'lodash/isEqual'
import isEmpty from 'lodash/isEmpty'
import { getFavoriteSkus } from 'components/MyFavorites/selectors'
import { addOeItemData } from 'components/OneColumnSearchResultPage/actions'
import { getElectricVehicleSpecifications } from 'components/common/ProductFlag/helpers'
import { getAllReviews, getNestedBVReviews } from 'components/common/hoc/withBazaarVoiceInlineRatings/selectors'
import { setFullReviews } from 'components/common/hoc/withBazaarVoiceInlineRatings/actions'
import { generateReviewsListForSchema } from 'components/TireLine/helpers.js'

const mapStateToProps = (state, { skuDetails: { frontTire: { listPrice, mapPrice } = {}, bvId, hasRearTire } = {}}) => {
  const search = (getPageLocation(state).search || '').slice(1)
  const { tab } = qs.parse(search)
  const schemaUrl = generateSchemaUrl(getLocationPathname(state))
  const allBVRatings = getAllReviews(state)
  const { average: averageRating = 0, total: totalRatingCount = 0 } = allBVRatings[bvId] || {}
  const nestedReviews = getNestedBVReviews(state)

  return {
    searchTab: tab,
    currentStoreNumber: getCurrentStoreNumber(state),
    currentTireSize: getActiveVehicleFrontTireSize(state),
    vehicleName: getActiveVehicleName(state),
    schemaUrl,
    offers: ((listPrice && !!getCurrentStore(state)) || (!getCurrentStore(state) && mapPrice)) && !hasRearTire ?
      {
        '@type': 'Offer',
        url: schemaUrl,
        priceCurrency: 'USD',
        price: listPrice || mapPrice || '',
        availability: 'https://schema.org/InStock',
        itemCondition: 'https://schema.org/NewCondition'
      } : undefined,
    aggregateRating: !hasRearTire ? {
      '@type': 'AggregateRating',
      ratingValue: averageRating,
      bestRating: 5,
      worstRating: 1,
      ratingCount: totalRatingCount
    } : undefined,
    review: nestedReviews && nestedReviews.length && !hasRearTire
      ? generateReviewsListForSchema(nestedReviews) : undefined,
    showCitiBanner: listPrice !== 0,
    favoriteSkus: getFavoriteSkus(state)
  }
}

const withTireCostInitialValues = withProps(({
  skuDetails: { hasRearTire, benefits = [], features = []} = {}}) => {
  const initialValues = {
    tireFrontQuantity: hasRearTire ? 2 : 4
  }

  if (hasRearTire) {
    initialValues.tireRearQuantity = 2
  }

  return {
    initialValues,
    skuDetailDescription: generateSchemaDescription(benefits, features)
  }
})

const withSetBadgeInformation = withStateHandlers({
  badgeTitle: undefined,
  badgeUpdating: false
}, {
  setBadgeTitle: () => ({ badgeTitle }) => ({ badgeTitle }),
  setBadgeUpdateStatus: () => ({ badgeUpdating }) => ({ badgeUpdating })
})

const withInitialization = lifecycle({
  componentDidMount() {
    const {
      setSkuId,
      skuDetails,
      skuDetails: {
        skuId,
        rearSkuId,
        hasRearTire,
        promotions,
        oeFlag,
        bvId
      } = {},
      gBBDetails,
      gBBAvailable,
      gBBTireSize,
      gBBStoreNumber,
      currentStoreNumber,
      currentTireSize,
      setBadgeTitle,
      setBadgeUpdateStatus,
      searchTab,
      updateGBBState,
      googleAnalyticProductDetailsView,
      googleAnalyticPromotionImpressions,
      favoriteSkus,
      addOeItemData,
      isEvItem,
      setFullReviews
    } = this.props
    const { title: badgeTitle = '' } = gBBDetails[skuId] || {}

    setFullReviews(bvId)

    if (searchTab === TIRE_DETAILS_REVIEWS_QUERY) {
      expandBazaarVoiceReviewTabAndScrollDown()
    }
    setSkuId(hasRearTire ? { skuId, rearSkuId } : { skuId })

    if ((isEmpty(gBBDetails) && gBBAvailable) ||
      currentTireSize !== gBBTireSize || currentStoreNumber !== gBBStoreNumber) {
      setBadgeUpdateStatus({ badgeUpdating: true })
      updateGBBState({ tireSize: currentTireSize, storeNumber: currentStoreNumber }).then(data => {
        setBadgeUpdateStatus({ badgeUpdating: false })
      })
    } else {
      setBadgeTitle({ badgeTitle })
      googleAnalyticProductDetailsView({
        data: getGTMData({
          product: { ...skuDetails, dimension2: badgeTitle },
          favoriteSkus,
          oeFlag,
          evFlag: isEvItem
        })
      })
    }
    googleAnalyticPromotionImpressions({ items: promotions, creative: 'tire-detail-promo-offers' })

    Boolean(oeFlag) && addOeItemData(skuId)
  },
  componentDidUpdate({
    gBBDetails: prevGBBDetails,
    currentTireSize: prevTireSize,
    currentStoreNumber: prevStoreNumber,
    skuDetails: { skuId: prevSkuId } = {}
  }) {
    const {
      gBBDetails,
      badgeTitle,
      badgeUpdating,
      currentStoreNumber,
      currentTireSize,
      gBBAvailable,
      updateGBBState,
      skuDetails,
      skuDetails: {
        skuId,
        promotions,
        oeFlag
      },
      setBadgeTitle,
      setBadgeUpdateStatus,
      googleAnalyticProductDetailsView,
      googleAnalyticPromotionImpressions,
      favoriteSkus,
      isEvItem
    } = this.props

    const { title: newBadgeTitle = '' } = gBBDetails[skuId] || {}

    if (!isEqual(gBBDetails, prevGBBDetails)
      || skuId !== prevSkuId || newBadgeTitle !== badgeTitle
    ) {
      if (!badgeUpdating) {
        setBadgeTitle({ badgeTitle: newBadgeTitle })
        googleAnalyticProductDetailsView({
          data: getGTMData({
            product: { ...skuDetails, dimension2: newBadgeTitle },
            favoriteSkus,
            oeFlag,
            evFlag: isEvItem
          })
        })
        googleAnalyticPromotionImpressions({ items: promotions, creative: 'tire-detail-promo-offers' })
      }
    }
    if ((isEmpty(gBBDetails) && gBBAvailable) ||
      prevTireSize !== currentTireSize || currentStoreNumber !== prevStoreNumber) {
      if (!badgeUpdating) {
        setBadgeUpdateStatus({ badgeUpdating: true })
        updateGBBState({ tireSize: currentTireSize, storeNumber: currentStoreNumber }).then(data => {
          setBadgeUpdateStatus({ badgeUpdating: false })
        })
      }
    }
  }
})

const withPromotionClickHandler = withHandlers({
  handlePromotionClick: ({ googleAnalyticPromotionClicks, showTireDetailsPromotion }) => ({ item, index }) => {
    showTireDetailsPromotion(item)
    googleAnalyticPromotionClicks({ item, index, creative: 'tire-detail-promo-offers' })
  }
})

const withBOTSkuDetailProps = withProps(({
  skuDetails: {
    specifications = []
  } = {}
}) => {
  const electricVehicleSpecifications = getElectricVehicleSpecifications(specifications)

  return {
    isEvItem: Boolean(electricVehicleSpecifications.length),
    itemEVType: electricVehicleSpecifications.length ? electricVehicleSpecifications[0].specValue : null
  }
})

export default compose(
  withType({ type: 'BOTSkuDetail' }),
  connect(mapStateToProps, {
    showTireDetailsPromotion,
    setSkuId,
    updateGBBState,
    googleAnalyticProductDetailsView,
    googleAnalyticPromotionImpressions,
    googleAnalyticPromotionClicks,
    addOeItemData,
    setFullReviews
  }),
  withGBBDetails,
  withSetBadgeInformation,
  withBOTSkuDetailProps,
  withTireCostInitialValues,
  withInitialization,
  withPromotionClickHandler
)(BOTSkuDetailComponent)
